import { DEFAULT_LORA_MODEL_WEIGHT } from 'src/components/GenerationTask/constants'
import type { LoraParametersInfo } from 'src/components/GenerationTask/MultiColumnGenerationForm/Generation/RightCol/LoraSelector/loraParameters'

export function stripLoraParametersTriggerWords(
  loraParameters: LoraParametersInfo[] | undefined,
) {
  if (loraParameters) {
    return loraParameters.map(info => ({
      versionId: info.versionId,
      weight: info.weight,
    }))
  }
  return undefined
}

export function cleanupLoraParameters(
  loraParameters: LoraParametersInfo[] | undefined,
  lora: Record<string, number> | undefined,
  hidePrompts: boolean | undefined,
): LoraParametersInfo[] | undefined {
  if (!lora) {
    return undefined
  }
  const loraParametersInfo: LoraParametersInfo[] = []
  const loraVersionIds = new Set(Object.keys(lora))
  if (loraParameters instanceof Array) {
    for (const info of loraParameters) {
      if (loraVersionIds.has(info.versionId)) {
        loraParametersInfo.push(info)
        loraVersionIds.delete(info.versionId)
      }
    }
  }
  for (const loraVersionId of loraVersionIds) {
    loraParametersInfo.push({
      versionId: loraVersionId,
      weight: lora[loraVersionId] ?? DEFAULT_LORA_MODEL_WEIGHT,
    })
  }
  if (hidePrompts) {
    return stripLoraParametersTriggerWords(loraParametersInfo)
  } else {
    return loraParametersInfo
  }
}

export interface PromptPlainTextSegment {
  type: 'plain-text'
  content: string
}

export interface PromptLoraTriggerWordsSegment {
  type: 'lora-trigger-words'
  modelId?: string
  versionId: string
  triggerWords?: string
}

export type PromptSegment =
  | PromptPlainTextSegment
  | PromptLoraTriggerWordsSegment

/**
 * Split prompt into plain text block and lora trigger words block using information in positionInfo.
 * Content between startIndex and endIndex will be replace to corresponded lora trigger words block.
 * For Lora without positionInfo, corresponded lora trigger words block will be appended to end of the block list
 *
 * For rich text prompt editor, the return value can be converted to document tree
 * For plain text prompt editor, the real prompt can be construct by
 * join the plain text and lora trigger words of each block in the return value with ", "
 */
export const parsePositionInfo = (
  prompt: string,
  loraParameters: LoraParametersInfo[] | undefined,
) => {
  const loraParametersWithPositionInfo: LoraParametersInfo[] = []
  const loraParametersWithoutPositionInfo: LoraParametersInfo[] = []
  for (const info of loraParameters ?? []) {
    if (info.positionInfo) {
      loraParametersWithPositionInfo.push(info)
    } else {
      loraParametersWithoutPositionInfo.push(info)
    }
  }
  loraParametersWithPositionInfo.sort(
    (info1, info2) =>
      info1.positionInfo!.startIndex - info2.positionInfo!.startIndex,
  )
  let lastEndIndex = 0
  for (const info of loraParametersWithPositionInfo) {
    if (info.positionInfo!.startIndex < lastEndIndex) {
      info.positionInfo!.startIndex = lastEndIndex
    }
    if (info.positionInfo!.endIndex < info.positionInfo!.startIndex) {
      info.positionInfo!.endIndex = info.positionInfo!.startIndex
    }
    lastEndIndex = info.positionInfo!.endIndex
  }
  const segments: PromptSegment[] = []
  let processedCharCount = 0
  for (const info of loraParametersWithPositionInfo) {
    const start = info.positionInfo!.startIndex
    const end = info.positionInfo!.endIndex
    if (start > processedCharCount) {
      const textSegment = prompt.slice(processedCharCount, start)
      if (textSegment) {
        segments.push({
          type: 'plain-text',
          content: textSegment,
        })
      }
    }
    segments.push({
      type: 'lora-trigger-words',
      versionId: info.versionId,
      triggerWords: info.triggerWords,
    })
    processedCharCount = end
  }
  if (processedCharCount < prompt.length) {
    segments.push({
      type: 'plain-text',
      content: prompt.slice(processedCharCount),
    })
  }
  for (const info of loraParametersWithoutPositionInfo) {
    segments.push({
      type: 'lora-trigger-words',
      versionId: info.versionId,
      triggerWords: info.triggerWords,
    })
  }
  return segments
}

export const mergePromptsWithTriggerWords = (
  prompt: string | undefined,
  loraParameters: LoraParametersInfo[] | undefined,
) => {
  if (loraParameters == undefined && prompt == undefined) {
    return undefined
  }
  return parsePositionInfo(prompt ?? '', loraParameters)
    .filter(segment => {
      if (segment.type === 'lora-trigger-words' && !segment.triggerWords) {
        return false
      }
      return true
    })
    .map(segment => {
      if (segment.type === 'lora-trigger-words') {
        return segment.triggerWords
      } else if (segment.type === 'plain-text') {
        return segment.content
      }
    })
    .join(',')
}
